import React, { useContext, useEffect, useRef, useState } from 'react'
import { ActionButton, ButtonModel, Pager } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

// types
import { MyPassesPageQuery, MyPassesPageSetQuery, PassesResponse } from '../../../../types/types'

// utils
import { PAGE_LINKS } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'
import { ACTIVE_PASSES_LIMIT_MY_PASSES_PAGE } from '../../../../utils/enums'

// components
import ErrorScreen from '../../../../atoms/ErrorScreen/ErrorScreen'
import Empty from '../../../../atoms/Empty/Empty'
import VisuallyHidden from '../../../../atoms/VisuallyHidden/VisuallyHidden'
import PassCard, { PassCardSkeleton } from '../PassCard/PassCard'

// assets
import EmptyPassesIcon from '../../../../assets/icons/EmptyPassesIcon'

// styles
import * as SC from '../PassCard/PassCardStyles'

// hooks
import useMessages from '../../../../hooks/useMessages'

type Props = {
	activePassesContainerRef: React.RefObject<HTMLElement>
	query: MyPassesPageQuery
	setQuery: MyPassesPageSetQuery
}

type FetchAnnounceMode = 'initial' | 'loadMore' | 'pageChange'

const ActivePasses = (props: Props) => {
	const { formatMessage, locale } = useIntl()
	const { query, setQuery, activePassesContainerRef } = props
	const { messages } = useMessages()
	const { apiBrowser } = useContext(AppContext)

	const [activePasses, setActivePasses] = useState<PassesResponse | undefined>()
	const [isLoading, setIsLoading] = useState(true)
	const [isFetchingFromLoadMoreButton, setIsFetchingFromLoadMoreButton] = useState(false)
	const [statusMessage, setStatusMessage] = useState('')
	const [refetchActivePasses, setRefetchActivePasses] = useState(false)
	const mergeWithPreviousActivePasses = useRef(false)
	const fetchAnnounceModeRef = useRef<FetchAnnounceMode>('initial')

	useEffect(() => {
		const fetchActivePasses = async () => {
			const announceMode = fetchAnnounceModeRef.current

			try {
				setIsLoading(true)
				setStatusMessage(messages.Loading)
				const activePassesResponse = await apiBrowser.b2c.getPasses({
					state: 'ACTIVE',
					page: query.activePassesPage,
					limit: ACTIVE_PASSES_LIMIT_MY_PASSES_PAGE
				})

				if (mergeWithPreviousActivePasses.current) {
					setActivePasses((prev) =>
						prev
							? {
									...prev,
									passes: [...prev.passes, ...activePassesResponse.passes],
									pagination: activePassesResponse.pagination
								}
							: prev
					)
				} else {
					setActivePasses(activePassesResponse)
				}

				if (!activePassesResponse.passes.length && query.activePassesPage === 1) {
					setStatusMessage(messages['You don’t have any active passes'])
				} else if (announceMode === 'loadMore') {
					setStatusMessage(
						formatMessage(
							{ id: '{count, plural, one {# more pass was loaded} other {# more passes were loaded}}' },
							{ count: activePassesResponse.passes.length }
						)
					)
				} else if (announceMode === 'pageChange') {
					setStatusMessage(
						formatMessage(
							{ id: 'Page {page} of {totalPages}' },
							{ page: activePassesResponse.pagination.page, totalPages: activePassesResponse.pagination.totalPages }
						)
					)
					activePassesContainerRef.current?.focus({ preventScroll: true })
				} else {
					setStatusMessage('')
				}
			} catch {
				setActivePasses(undefined)
				setStatusMessage(messages['Something went wrong. Data was not loaded.'])
			} finally {
				setIsLoading(false)
				setIsFetchingFromLoadMoreButton(false)
				mergeWithPreviousActivePasses.current = false
				fetchAnnounceModeRef.current = 'initial'
			}
		}

		fetchActivePasses()
	}, [activePassesContainerRef, apiBrowser, formatMessage, messages, query.activePassesPage, refetchActivePasses])

	const fetchActivePassesFromLoadMoreBtn = () => {
		setIsFetchingFromLoadMoreButton(true)
		fetchAnnounceModeRef.current = 'loadMore'

		// set mergeWithPreviousActivePasses flag to true so the currently showed active passes can be merged with newly loaded active passes
		mergeWithPreviousActivePasses.current = true

		setQuery({
			...query,
			activePassesPage: query.activePassesPage + 1
		})
	}

	const handleActivePassesPageChange = (newPage: number) => {
		fetchAnnounceModeRef.current = 'pageChange'
		activePassesContainerRef.current?.scrollIntoView({ behavior: 'smooth' })
		setQuery({ ...query, activePassesPage: newPage })
	}

	const handleRetryFetchingActivePasses = () => {
		fetchAnnounceModeRef.current = 'initial'

		if (query.activePassesPage !== 1) {
			setQuery({ ...query, activePassesPage: 1 })
		} else {
			setRefetchActivePasses((current) => !current)
		}
	}

	const remainingActivePassesCount = activePasses ? activePasses.pagination.totalCount - activePasses.pagination.page * activePasses.pagination.limit : 0

	const showMoreActivePassesButtonText = formatMessage(
		{ id: 'Show more passes ({remainingPassesCount})' },
		{ remainingPassesCount: remainingActivePassesCount }
	)

	const isListBusy = isLoading || isFetchingFromLoadMoreButton

	const getContent = () => {
		if (isLoading && !isFetchingFromLoadMoreButton) {
			const skeletonItems = Array.from({ length: 12 }, (_, index) => <PassCardSkeleton key={index} />)

			return <SC.PassesGrid aria-busy={true}>{skeletonItems}</SC.PassesGrid>
		}

		if (typeof activePasses === 'undefined') {
			return <ErrorScreen button={{ onClick: handleRetryFetchingActivePasses }} />
		}

		if (!activePasses.passes.length) {
			return <Empty title={messages['You don’t have any active passes']} icon={<EmptyPassesIcon aria-hidden />} />
		}

		return (
			<>
				<SC.PassesGrid aria-busy={isListBusy || undefined}>
					{activePasses.passes.map((pass) => (
						<PassCard key={pass.id} pass={pass} href={PAGE_LINKS['/salons/passes/:passID'](locale, pass.id)} />
					))}
				</SC.PassesGrid>
				{activePasses.passes.length > 0 && activePasses.pagination.totalPages > 1 && (
					<SC.BottomNavigation aria-label={messages['Active passes pagination']}>
						{activePasses.passes.length > 0 && query.activePassesPage !== activePasses.pagination.totalPages && (
							<ActionButton
								isLoading={isFetchingFromLoadMoreButton}
								buttonStyle={ButtonModel.Styles.secondary}
								onClick={fetchActivePassesFromLoadMoreBtn}
							>
								{showMoreActivePassesButtonText}
							</ActionButton>
						)}

						<SC.PaginationWrapper>
							<Pager
								currentPage={query.activePassesPage}
								lastPage={activePasses.pagination.totalPages}
								onPageChange={handleActivePassesPageChange}
							/>
						</SC.PaginationWrapper>
					</SC.BottomNavigation>
				)}
			</>
		)
	}

	return (
		<>
			<VisuallyHidden as='div' role='status' aria-live='polite' aria-atomic='true'>
				{statusMessage}
			</VisuallyHidden>
			{getContent()}
		</>
	)
}

export default ActivePasses
